import { Flex } from "@chakra-ui/react";
import {
  decimalToPercentage,
  toCurrency,
  toNumberDisplayValue,
} from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "components";
import { DisplayPerformanceErrorBoundary } from "../../DisplayPerformanceErrorBoundary";
import { FaHashtag } from "react-icons/fa";
import { DisplayPerformanceFilters } from "../../types";
import { NOT_A_CPA_CAMPAIGN_MESSAGE } from "../../../displayPerformance.utils";
import { CampaignEngagementRequests } from "../../requests.types";
import {
  ClickThrough,
  ImpressionsAndClicksOverTime,
  MediaDeliveryMap,
  MobileDeliveryBreakdown,
  OverallViewability,
  PerformanceByDevice,
  SiteEngagementLift,
  TopCreativePerformance,
} from "./components";

type CampaignEngagementProps = {
  campaignName: string;
  id: string;
  filters: DisplayPerformanceFilters;
  requests: CampaignEngagementRequests;
  isBasicView?: boolean;
};

const CampaignEngagement = ({
  id,
  filters,
  campaignName,
  requests,
  isBasicView,
}: CampaignEngagementProps) => {
  const { data, isFetching: isLoading } = useQuery(
    ["impressionsClicksPerformance-statistics", id, filters],
    () => requests.getStatistics(id, filters)
  );

  return (
    <>
      <Flex w="100%" gap={4} mb={4} minH="200px">
        {!isBasicView && (
          <DisplayPerformanceErrorBoundary>
            <StatCard
              isLoading={isLoading}
              title="Overall Spend"
              colorScheme="yellow"
              value={data?.overallSpend}
              valueFormatter={(value) => toCurrency(value)}
            />
          </DisplayPerformanceErrorBoundary>
        )}

        <DisplayPerformanceErrorBoundary>
          <StatCard
            isLoading={isLoading}
            title="Total Impressions"
            value={data?.totalImpressions}
            previousPeriodChange={data?.totalImpressionsPreviousPeriod}
            valueFormatter={(value) => toNumberDisplayValue(value)}
          />
        </DisplayPerformanceErrorBoundary>

        <DisplayPerformanceErrorBoundary>
          <StatCard
            isLoading={isLoading}
            title="Total Clicks"
            value={data?.totalClicks}
            previousPeriodChange={data?.totalClicksPreviousPeriod}
            valueFormatter={(value) => toNumberDisplayValue(value)}
          />
        </DisplayPerformanceErrorBoundary>
      </Flex>

      <Flex w="100%" gap={4} mb={4} minH="200px">
        <DisplayPerformanceErrorBoundary>
          <StatCard
            isLoading={isLoading}
            title="Overall CTR"
            value={data?.overallCTR}
            previousPeriodChange={data?.overallCTRPreviousPeriod}
            valueFormatter={(value) =>
              `${toNumberDisplayValue(decimalToPercentage(value))}%`
            }
          />
        </DisplayPerformanceErrorBoundary>

        {!isBasicView && (
          <DisplayPerformanceErrorBoundary>
            <StatCard
              isLoading={isLoading}
              title="Total Conversions"
              value={data?.totalConversions}
              previousPeriodChange={data?.totalConversionsPreviousPeriod}
              valueFormatter={(value) => toNumberDisplayValue(value)}
              hasData={data?.totalConversions !== 0}
              noDataIcon={FaHashtag}
              noDataMessage={NOT_A_CPA_CAMPAIGN_MESSAGE}
            />
          </DisplayPerformanceErrorBoundary>
        )}

        {!isBasicView && (
          <OverallViewability
            campaignId={id}
            filters={filters}
            getOverallViewability={
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              requests.getOverallViewability!
            }
          />
        )}
      </Flex>

      <Flex w="100%" gap={4} mb={4} minH="200px">
        <DisplayPerformanceErrorBoundary>
          <ImpressionsAndClicksOverTime
            campaignId={id}
            isLoading={isLoading}
            data={data?.impressionsAndClicksOverTime ?? []}
          />
        </DisplayPerformanceErrorBoundary>

        {!isBasicView && (
          <TopCreativePerformance
            campaignId={id}
            filters={filters}
            topCreativePerformance={
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              requests.topCreativePerformance!
            }
          />
        )}

        {!isBasicView && (
          <ClickThrough
            totalConversions={data?.totalConversions ?? 0}
            campaignId={id}
            filters={filters}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            getEngagementBreakdown={requests.getEngagementBreakdown!}
          />
        )}
      </Flex>

      <Flex w="100%" gap={4} mb={4}>
        <PerformanceByDevice
          campaignId={id}
          filters={filters}
          getPerformanceByDevice={requests.getPerformanceByDevice}
        />
        <MobileDeliveryBreakdown
          campaignId={id}
          filters={filters}
          getMobileDeliveryBreakdown={requests.getMobileDeliveryBreakdown}
        />
      </Flex>

      <Flex w="100%" gap={4} mb={4} minH="200px">
        <SiteEngagementLift
          campaignId={id}
          campaignName={campaignName}
          getSiteEngagementLift={requests.getSiteEngagementLift}
        />
      </Flex>

      <MediaDeliveryMap
        campaignId={id}
        filters={filters}
        getImpressionsByLocation={requests.getImpressionsByLocation}
        getTopicsByLocation={requests.getTopicsByLocation}
      />
    </>
  );
};

export { CampaignEngagement };
